"use client";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  createServiceRequest,
  getCatalogServiceBySlug,
  getCatalogServices,
  getMyPractices,
  getMyServiceRequests,
  getServiceCategories,
} from "@/lib/services/api";
import type { ContactMethod, ServiceRequestTopic } from "@/lib/services/types";
import { queryKeys } from "@/lib/query-keys";
import { MissingSupabaseConfigError } from "@/lib/supabase/env";

function retryUnlessMissingConfig(failureCount: number, error: unknown) {
  return error instanceof MissingSupabaseConfigError ? false : failureCount < 1;
}

export function useServiceCategories(enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.serviceCategories,
    queryFn: getServiceCategories,
    enabled,
    retry: retryUnlessMissingConfig,
  });
}

export function useCatalogServices(enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.catalogServices,
    queryFn: getCatalogServices,
    enabled,
    retry: retryUnlessMissingConfig,
  });
}

export function useCatalogService(slug: string, enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.catalogService(slug),
    queryFn: () => getCatalogServiceBySlug(slug),
    enabled: enabled && Boolean(slug),
    retry: retryUnlessMissingConfig,
  });
}

export function useMyPractices(enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.myPractices,
    queryFn: getMyPractices,
    enabled,
    retry: retryUnlessMissingConfig,
  });
}

export function useServiceRequests(enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.serviceRequests,
    queryFn: getMyServiceRequests,
    enabled,
    retry: retryUnlessMissingConfig,
  });
}

export function useCreateServiceRequest() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: {
      userId: string;
      serviceId: string;
      practiceId: string;
      topic: ServiceRequestTopic;
      description: string;
      contactMethod: ContactMethod;
      contactPhone?: string;
      preferredContactAt?: string | null;
      idempotencyKey: string;
      termsAccepted?: boolean;
      termsVersion?: string | null;
      termsOpenedAt?: string | null;
    }) => createServiceRequest(input),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: queryKeys.serviceRequests }),
        queryClient.invalidateQueries({ queryKey: queryKeys.adminLeads }),
        queryClient.invalidateQueries({ queryKey: queryKeys.myCustomers }),
        queryClient.invalidateQueries({ queryKey: queryKeys.adminCustomers }),
      ]);
    },
  });
}
